/* eslint-disable no-console */
import { cloneDeep, concat, get, isEmpty } from 'lodash';
import type from './Chat.type';
import { Page_Size as PageSize } from '../../../Components/helper';

const setArrayByCase = (array, payload) => {
  let newArray;

  // Handle Load More (isScrolled = true) - prepend older messages
  if (payload.isScrolled && payload.data && payload.data.length > 0) {
    // Flatten messageGroups if present
    const flatData = Array.isArray(payload.data) && payload.data[0]?.messages ? payload.data.flatMap((group) => group.messages || []) : payload.data;
    // Prepend older messages to the beginning
    newArray = [...flatData, ...array];
  } else if (!isEmpty(get(payload, 'searchText')) && get(payload, 'isScrolled')) {
    newArray = [...array, ...payload.data];
  } else if (!isEmpty(get(payload, 'searchText'))) {
    // Flatten messageGroups for search results
    const flatData = Array.isArray(payload.data) && payload.data[0]?.messages ? payload.data.flatMap((group) => group.messages || []) : payload.data;
    newArray = flatData;
  } else if (get(payload, 'isScrolled')) {
    newArray = concat(array, payload.data);
  } else {
    // Flatten messageGroups for initial load
    const flatData = Array.isArray(payload.data) && payload.data[0]?.messages ? payload.data.flatMap((group) => group.messages || []) : payload.data;
    console.log('🚀 ~ flatData:', flatData);
    newArray = flatData;
  }
  return newArray;
};

const initialState = {
  // Data arrays
  messagesList: [],
  participantsList: [],
  roomsList: [],

  // Single entities
  activeRoom: null,
  currentMessage: null,

  // Counts and metadata
  totalMessageCount: 0,
  unreadMessageCount: 0,
  totalParticipants: 0,

  // Real-time state
  typingUsers: [],
  onlineUsers: [],

  // Filter and pagination
  filterValues: {
    pageIndex: 1,
    pageSize: PageSize,
    roomName: '',
    searchText: '',
    messageType: 'all', // all, text, image, file
    dateRange: {
      startDate: null,
      endDate: null,
    },
  },

  // Load More pagination state
  pagination: {
    currentPage: 1,
    pageSize: PageSize,
    totalCount: 0,
    totalPages: 0,
    hasMoreMessages: true,
  },

  // Loading and error states
  loading: false,
  error: null,
  messageLoading: false,
  participantsLoading: false,
};

// eslint-disable-next-line default-param-last
const ChatReducer = (state = initialState, action) => {
  switch (action.type) {
    case type.FETCH_CHAT_MESSAGES: {
      const updatedMessagesArray = cloneDeep(setArrayByCase(state.messagesList, action.payload));

      return {
        ...state,
        messagesList: updatedMessagesArray,
        totalMessageCount: action?.payload?.totalCount || 0,
        // Update pagination state
        pagination: {
          ...state.pagination,
          ...action?.payload?.pagination,
          hasMoreMessages: action?.payload?.pagination?.currentPage < action?.payload?.pagination?.totalPages,
        },
        messageLoading: false,
        error: null,
      };
    }

    case type.FETCH_CHAT_PARTICIPANTS:
      return {
        ...state,
        participantsList: action?.payload?.data || [],
        totalParticipants: action?.payload?.totalCount || 0,
        participantsLoading: false,
        error: null,
      };

    case type.FETCH_UNREAD_MESSAGE_COUNT:
      return {
        ...state,
        unreadMessageCount: action?.payload?.count || 0,
        error: null,
      };

    case type.SEND_CHAT_MESSAGE:
      return {
        ...state,
        messagesList: [...state.messagesList, action.payload],
        totalMessageCount: state.totalMessageCount + 1,
        error: null,
      };

    case type.ADD_NEW_MESSAGE: {
      // Add new message from real-time updates
      const messageExists = state.messagesList.find((msg) => msg._id === action.payload._id);
      if (messageExists) {
        return state;
      }
      return {
        ...state,
        messagesList: [...state.messagesList, action.payload],
        totalMessageCount: state.totalMessageCount + 1,
        unreadMessageCount: state.unreadMessageCount + 1,
      };
    }

    case type.UPDATE_CHAT_MESSAGE: {
      const updatedMessages = state.messagesList.map((message) => (message._id === action.payload._id ? { ...message, ...action.payload } : message));
      return {
        ...state,
        messagesList: updatedMessages,
        error: null,
      };
    }

    case type.DELETE_CHAT_MESSAGE: {
      const filteredMessages = state.messagesList.filter((message) => message._id !== action.payload.messageId);
      return {
        ...state,
        messagesList: filteredMessages,
        totalMessageCount: state.totalMessageCount - 1,
        error: null,
      };
    }

    case type.MARK_MESSAGES_AS_READ: {
      const markedMessages = state.messagesList.map((message) => ({
        ...message,
        isRead: true,
      }));
      return {
        ...state,
        messagesList: markedMessages,
        unreadMessageCount: 0,
        error: null,
      };
    }

    case type.UPDATE_TYPING_STATUS:
      return {
        ...state,
        typingUsers: action.payload.isTyping
          ? [...state.typingUsers.filter((user) => user.userId !== action.payload.userId), action.payload]
          : state.typingUsers.filter((user) => user.userId !== action.payload.userId),
      };

    case type.SET_ACTIVE_ROOM:
      return {
        ...state,
        activeRoom: action.payload,
        messagesList: [], // Clear messages when switching rooms
        totalMessageCount: 0,
        unreadMessageCount: 0,
        // Reset pagination when switching rooms
        pagination: {
          ...initialState.pagination,
        },
      };

    case type.SET_CHAT_FILTER_DATA:
      return {
        ...state,
        filterValues: { ...state.filterValues, ...action.payload },
      };

    case type.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case type.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
        messageLoading: false,
        participantsLoading: false,
      };

    case type.CLEAR_MESSAGES:
      return {
        ...state,
        messagesList: [],
        totalMessageCount: 0,
        unreadMessageCount: 0,
      };

    case type.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case type.CHAT_CLEAR_REDUCER:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

export default ChatReducer;
